export interface NavigationState {
  level1: string;
  level2: string;
  level3: string;
  level4: string;
  level5: string;
  code: string;
}

// Navigation hierarchy constants
const MAX_NAV_LEVEL = 5; // Maximum depth of navigation hierarchy

const STORAGE_KEY = "navigation_hierarchy";

const isLocalStorageAvailable = () => {
  try {
    return typeof window !== "undefined" && window.localStorage !== undefined;
  } catch (_e) {
    return false;
  }
};

export class NavigationDataLayerStore {
  private state: NavigationState;

  constructor() {
    this.state = this.loadState();
  }

  private loadState(): NavigationState {
    if (isLocalStorageAvailable()) {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        return JSON.parse(savedState);
      }
    }
    return {
      level1: "",
      level2: "",
      level3: "",
      level4: "",
      level5: "",
      code: "",
    };
  }

  private saveState(): void {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    }
  }

  // Set the selected item for a specific level
  setLevel(level: 1 | 2 | 3 | 4 | 5, item: string, index: number): void {
    const levelKey = `level${level}` as keyof NavigationState;
    this.state[levelKey] = item;

    // Update code
    const codes = this.state.code.split(".");
    codes[level - 1] = (index + 1).toString();
    // Trim any subsequent codes when setting a new level
    codes.length = level;
    this.state.code = codes.join(".");

    // Clear all subsequent levels
    for (let i = level + 1; i <= MAX_NAV_LEVEL; i++) {
      const subsequentLevelKey = `level${i}` as keyof NavigationState;
      this.state[subsequentLevelKey] = "";
    }

    this.saveState();
  }

  // Get the selected item for a specific level
  getLevel(level: 1 | 2 | 3 | 4 | 5): string {
    const levelKey = `level${level}` as keyof NavigationState;
    return this.state[levelKey];
  }

  // Clear all levels
  clearAll(): void {
    this.state = {
      level1: "",
      level2: "",
      level3: "",
      level4: "",
      level5: "",
      code: "",
    };
    this.saveState();
  }

  // Clear from a specific level onwards
  clearFromLevel(level: 1 | 2 | 3 | 4 | 5): void {
    // Clear levels
    for (let i = level; i <= MAX_NAV_LEVEL; i++) {
      const levelKey = `level${i}` as keyof NavigationState;
      this.state[levelKey] = "";
    }

    // Adjust code to match cleared levels
    const codes = this.state.code.split(".");
    codes.length = level - 1; // Trim code array to keep only levels before the cleared level
    this.state.code = codes.join(".");

    this.saveState();
  }

  // Get the entire navigation state
  getState(): NavigationState {
    return { ...this.state };
  }
}

// Export a singleton instance
export const navigationStore = new NavigationDataLayerStore();
